import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import apiService from '../../services/api';
import {
  DailyDCModule,
  DateWiseDCModule,
  Coach,
  CreateCoachRequest,
} from '../../types/auth';
import { ExperimentDailyModule } from '../../types';
import CoachModal from '../../components/CoachModal/CoachModal';
import './DailyChallengeDetailsPage.css';

const DailyChallengeDetailsPage: React.FC = () => {
  const { challengeId } = useParams<{ challengeId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<
    'date-modules' | 'daily-modules' | 'coaches' | 'reset'
  >('daily-modules');
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showDateModuleModal, setShowDateModuleModal] = useState(false);
  const [moduleForm, setModuleForm] = useState<{
    title: string;
    medal_share_image_url: string;
    medal_image_url: string;
    medal_joy_audio_url: string;
    medal_title: string;
    medal_subtitle: string;
    medal_share_text: string;
    medal_share_url: string;
    ai_coach_context: string;
    children_experiment_module_id: string[];
    gold_medal_image_url: string;
    silver_medal_image_url: string;
    bronze_medal_image_url: string;
    total_available_points: string;
  }>({
    title: '',
    medal_share_image_url: '',
    medal_image_url: '',
    medal_joy_audio_url: '',
    medal_title: '',
    medal_subtitle: '',
    medal_share_text: '',
    medal_share_url: '',
    ai_coach_context: '',
    children_experiment_module_id: [''],
    gold_medal_image_url: '',
    silver_medal_image_url: '',
    bronze_medal_image_url: '',
    total_available_points: '',
  });
  const [editingDailyModule, setEditingDailyModule] = useState<{
    module_id: string;
    title: string;
    medal_share_image_url: string;
    medal_image_url: string;
    medal_joy_audio_url: string;
    medal_title: string;
    medal_subtitle: string;
    medal_share_text: string;
    medal_share_url: string;
    ai_coach_context: string;
    children_experiment_module_id?: string[];
    gold_medal_image_url?: string;
    silver_medal_image_url?: string;
    bronze_medal_image_url?: string;
    total_available_points?: string;
  } | null>(null);
  const [dateModuleForm, setDateModuleForm] = useState({
    event_date: '',
    title: '',
  });
  const [editingDateModule, setEditingDateModule] = useState<{
    module_id: string;
    event_date: string;
    title: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dailyModules, setDailyModules] = useState<DailyDCModule[]>([]);
  const [dateModules, setDateModules] = useState<DateWiseDCModule[]>([]);
  const [loading, setLoading] = useState(false);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [showCoachModal, setShowCoachModal] = useState(false);
  const [editingCoach, setEditingCoach] = useState<Coach | null>(null);
  const [resetForm, setResetForm] = useState({
    phone_number: '',
    day_to_reset: '',
    post_to_reset: '',
  });
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [experimentModules, setExperimentModules] = useState<
    ExperimentDailyModule[]
  >([]);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [updatingChallengePoints, setUpdatingChallengePoints] = useState(false);

  useEffect(() => {
    if (challengeId) {
      if (activeTab === 'daily-modules') {
        fetchDailyModules();
      } else if (activeTab === 'date-modules') {
        fetchDateModules();
      } else if (activeTab === 'coaches') {
        fetchCoaches();
      }
    }
  }, [challengeId, activeTab]);

  const fetchDailyModules = async () => {
    try {
      setLoading(true);
      const modules = await authService.getDailyDCModules(challengeId!);
      setDailyModules(modules);
    } catch (error) {
      console.error('Error fetching daily modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDateModules = async () => {
    try {
      setLoading(true);
      const modules = await authService.getDateWiseDCModules(challengeId!);
      setDateModules(modules);
    } catch (error) {
      console.error('Error fetching date modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoaches = async () => {
    try {
      setLoading(true);
      const response = await authService.getCoachesByChallengeId(challengeId!);
      setCoaches(response.coaches);
    } catch (error) {
      console.error('Error fetching coaches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExperimentModules = async () => {
    try {
      const response = await apiService.getExperimentDailyModules();
      setExperimentModules(response.data || []);
    } catch (error) {
      console.error('Error fetching experiment modules:', error);
    }
  };

  const handleAddModule = async () => {
    setEditingDailyModule(null);
    setModuleForm({
      title: '',
      medal_share_image_url: '',
      medal_image_url: '',
      medal_joy_audio_url: '',
      medal_title: '',
      medal_subtitle: '',
      medal_share_text: '',
      medal_share_url: '',
      ai_coach_context: '',
      children_experiment_module_id: [''],
      gold_medal_image_url: '',
      silver_medal_image_url: '',
      bronze_medal_image_url: '',
      total_available_points: '',
    });
    await fetchExperimentModules();
    setShowModuleModal(true);
    setError(null);
    document.body.style.overflow = 'hidden';
  };

  const handleEditDailyModule = async (module: {
    module_id: string;
    title: string;
    medal_share_image_url: string;
    medal_image_url: string;
    medal_joy_audio_url: string;
    medal_title: string;
    medal_subtitle: string;
    medal_share_text: string;
    medal_share_url: string;
    ai_coach_context: string;
    children_experiment_module_id?: string[];
    gold_medal_image_url?: string;
    silver_medal_image_url?: string;
    bronze_medal_image_url?: string;
    total_available_points?: string;
  }) => {
    setEditingDailyModule(module);
    setModuleForm({
      title: module.title,
      medal_share_image_url: module.medal_share_image_url,
      medal_image_url: module.medal_image_url,
      medal_joy_audio_url: module.medal_joy_audio_url,
      medal_title: module.medal_title,
      medal_subtitle: module.medal_subtitle,
      medal_share_text: module.medal_share_text,
      medal_share_url: module.medal_share_url,
      ai_coach_context: module.ai_coach_context,
      children_experiment_module_id: module.children_experiment_module_id || [
        '',
      ],
      gold_medal_image_url: module.gold_medal_image_url || '',
      silver_medal_image_url: module.silver_medal_image_url || '',
      bronze_medal_image_url: module.bronze_medal_image_url || '',
      total_available_points: module.total_available_points || '',
    });
    await fetchExperimentModules();
    setShowModuleModal(true);
    setError(null);
    document.body.style.overflow = 'hidden';
  };

  const handleAddDateModule = () => {
    setEditingDateModule(null);
    setDateModuleForm({
      event_date: '',
      title: '',
    });
    setShowDateModuleModal(true);
    setError(null);
    document.body.style.overflow = 'hidden';
  };

  const handleEditDateModule = (module: {
    module_id: string;
    event_date: string;
    title: string;
  }) => {
    setEditingDateModule(module);
    setDateModuleForm({
      event_date: module.event_date,
      title: module.title,
    });
    setShowDateModuleModal(true);
    setError(null);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setShowModuleModal(false);
    setEditingDailyModule(null);
    setModuleForm({
      title: '',
      medal_share_image_url: '',
      medal_image_url: '',
      medal_joy_audio_url: '',
      medal_title: '',
      medal_subtitle: '',
      medal_share_text: '',
      medal_share_url: '',
      ai_coach_context: '',
      children_experiment_module_id: [''],
      gold_medal_image_url: '',
      silver_medal_image_url: '',
      bronze_medal_image_url: '',
      total_available_points: '',
    });
    setError(null);
    setSubmitting(false);
    document.body.style.overflow = 'unset';
  };

  const handleModuleClick = (moduleId: string, moduleType: string) => {
    navigate(
      `/daily-challenges/${challengeId}/module/${moduleId}?type=${moduleType}`
    );
  };

  const handleCloseDateModal = () => {
    setShowDateModuleModal(false);
    setEditingDateModule(null);
    setDateModuleForm({
      event_date: '',
      title: '',
    });
    setError(null);
    setSubmitting(false);
    document.body.style.overflow = 'unset';
  };

  const handleSubmitModule = async () => {
    try {
      setSubmitting(true);
      setError(null);

      if (!moduleForm.title.trim()) {
        setError('Title is required');
        return;
      }

      const payload = {
        ...moduleForm,
        children_experiment_module_id: moduleForm.children_experiment_module_id
          .filter(id => id.trim())
          .join(','),
      };

      const apiPayload: any = {
        ...payload,
        children_experiment_module_id: payload.children_experiment_module_id
          ? payload.children_experiment_module_id
              .split(',')
              .map(id => id.trim())
              .filter(id => id)
          : undefined,
      };

      if (
        !moduleForm.total_available_points ||
        (typeof moduleForm.total_available_points === 'string' &&
          !moduleForm.total_available_points.trim())
      ) {
        apiPayload.total_available_points = null;
      }

      if (editingDailyModule) {
        await authService.updateDailyModule({
          module_id: editingDailyModule.module_id,
          ...apiPayload,
        });
      } else {
        await authService.createDailyModule({
          dc_id: challengeId!,
          ...apiPayload,
        });
      }

      await fetchDailyModules();
      document.body.style.overflow = 'unset';
      handleCloseModal();
    } catch (error) {
      console.error(
        `Error ${editingDailyModule ? 'updating' : 'creating'} module:`,
        error
      );
      setError(
        `Failed to ${editingDailyModule ? 'update' : 'create'} module. Please try again.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitDateModule = async () => {
    try {
      setSubmitting(true);
      setError(null);

      if (!dateModuleForm.title.trim()) {
        setError('Title is required');
        return;
      }

      if (!dateModuleForm.event_date) {
        setError('Event date is required');
        return;
      }

      if (editingDateModule) {
        await authService.updateDateWiseModule({
          module_id: editingDateModule.module_id,
          ...dateModuleForm,
        });
      } else {
        await authService.createDateWiseModule({
          dc_id: challengeId!,
          ...dateModuleForm,
        });
      }

      await fetchDateModules();
      document.body.style.overflow = 'unset';
      handleCloseDateModal();
    } catch (error) {
      console.error(
        `Error ${editingDateModule ? 'updating' : 'creating'} date module:`,
        error
      );
      setError(
        `Failed to ${editingDateModule ? 'update' : 'create'} date module. Please try again.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCoach = () => {
    setEditingCoach(null);
    setShowCoachModal(true);
    document.body.style.overflow = 'hidden';
  };

  const handleEditCoach = (coach: Coach) => {
    setEditingCoach(coach);
    setShowCoachModal(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseCoachModal = () => {
    setShowCoachModal(false);
    setEditingCoach(null);
    document.body.style.overflow = 'unset';
  };

  const handleSaveCoach = async (coachData: CreateCoachRequest) => {
    try {
      const payload = { ...coachData, entity_id: challengeId };
      delete payload.quest_id;

      if (editingCoach) {
        await authService.updateCoach(editingCoach.id, payload);
      } else {
        await authService.createCoach(payload);
      }

      await fetchCoaches();
      handleCloseCoachModal();
    } catch (error) {
      console.error('Error saving coach:', error);
    }
  };

  const handleDeleteDailyModule = async (moduleId: string) => {
    if (!window.confirm('Are you sure you want to delete this module?')) {
      return;
    }

    try {
      await authService.deleteDailyModule(moduleId);
      await fetchDailyModules();
    } catch (error) {
      console.error('Error deleting daily module:', error);
      setError('Failed to delete module. Please try again.');
    }
  };

  const handleDeleteDateModule = async (moduleId: string) => {
    if (!window.confirm('Are you sure you want to delete this module?')) {
      return;
    }

    try {
      await authService.deleteDateWiseModule(moduleId);
      await fetchDateModules();
    } catch (error) {
      console.error('Error deleting date module:', error);
      setError('Failed to delete module. Please try again.');
    }
  };

  const handleResetChallenge = async () => {
    try {
      setResetting(true);
      setResetError(null);
      setResetSuccess(null);

      if (!resetForm.phone_number.trim()) {
        setResetError('Phone number is required');
        return;
      }

      if (!resetForm.day_to_reset.trim()) {
        setResetError('Day to reset to is required');
        return;
      }

      if (!resetForm.post_to_reset.trim()) {
        setResetError('Post ID is required');
        return;
      }

      const phoneNumber = '91' + resetForm.phone_number;
      const dayToReset = parseInt(resetForm.day_to_reset) - 1;

      const response = await authService.resetDailyChallenge({
        phone_number: phoneNumber,
        dc_id: challengeId!,
        day_to_reset_to: dayToReset,
        post_id: resetForm.post_to_reset,
      });

      setResetSuccess('Challenge reset successfully!');
      setResetForm({
        phone_number: '',
        day_to_reset: '',
        post_to_reset: '',
      });
    } catch (error) {
      console.error('Error resetting challenge:', error);
      setResetError('Failed to reset challenge. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  const handleUploadExcel = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    input.onchange = e => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setSelectedFile(file);
        setShowConfirmModal(true);
      }
    };
    input.click();
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;

    setShowConfirmModal(false);
    setUploadingExcel(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        accept: 'application/json',
      };

      if (token) {
        headers['x-auth-token'] = token;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/scripts/challenge-post-creation`,
        {
          method: 'POST',
          headers,
          body: formData,
        }
      );

      const result = await response.json();
      setUploadResult(result);
      setShowUploadModal(true);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadResult({ error: 'Upload failed. Please try again.' });
      setShowUploadModal(true);
    } finally {
      setUploadingExcel(false);
      setSelectedFile(null);
    }
  };

  const handleUpdateChallengePoints = async () => {
    try {
      setUpdatingChallengePoints(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/mastery-challenges/challenge-point-update/${challengeId}`,
        {
          method: 'PATCH',
          headers: {
            accept: 'application/json',
            ...(token && { 'x-auth-token': token }),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update challenge points');
      }

      alert('Challenge points updated successfully!');
    } catch (error) {
      console.error('Error updating challenge points:', error);
      alert('Failed to update challenge points. Please try again.');
    } finally {
      setUpdatingChallengePoints(false);
    }
  };

  const renderTabContent = () => {
    if (activeTab === 'date-modules') {
      return (
        <div className="modules-content">
          <div className="content-header">
            <div className="header-content">
              <h1 className="page-title">Date Specific Modules</h1>
              <p className="page-subtitle">Manage date specific modules</p>
            </div>
            <button className="upload-excel-button" onClick={handleUploadExcel}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10,9 9,9 8,9" />
              </svg>
              Upload Excel
            </button>
            <button
              className="create-button"
              onClick={handleUpdateChallengePoints}
              disabled={updatingChallengePoints}
            >
              {updatingChallengePoints
                ? 'Updating...'
                : 'Update challenge points'}
            </button>
            <button className="create-button" onClick={handleAddDateModule}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Module
            </button>
          </div>
          {loading ? (
            <div className="empty-state">
              <div className="button-spinner"></div>
              <p>Loading modules...</p>
            </div>
          ) : dateModules.length > 0 ? (
            <div className="modules-list">
              {dateModules.map(module => (
                <div
                  key={module.id}
                  className="module-card"
                  onClick={() => handleModuleClick(module.id, 'date')}
                >
                  <div className="module-card-header">
                    <div>
                      <h3>{module.title}</h3>
                      <p className="challenge-id">
                        DC ID: {challengeId}{' '}
                        <svg
                          onClick={e => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(challengeId || '');
                          }}
                          style={{ cursor: 'pointer', marginLeft: '4px' }}
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <title>Copy DC ID</title>
                          <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                          />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </p>
                    </div>
                    <div className="module-actions">
                      <button
                        className="edit-module-button"
                        onClick={e => {
                          e.stopPropagation();
                          handleEditDateModule({
                            module_id: module.id,
                            event_date: module.event_date,
                            title: module.title,
                          });
                        }}
                        title="Edit module"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        className="delete-module-button"
                        onClick={e => {
                          e.stopPropagation();
                          handleDeleteDateModule(module.id);
                        }}
                        title="Delete module"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="3,6 5,6 21,6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="module-card-body">
                    <div className="module-info">
                      <p>
                        <strong>ID:</strong> {module.id}{' '}
                        <svg
                          onClick={e => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(module.id);
                          }}
                          style={{ cursor: 'pointer', marginLeft: '8px' }}
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <title>Copy ID</title>
                          <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                          />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </p>
                      <p>
                        <strong>DC ID:</strong> {challengeId}{' '}
                        <svg
                          onClick={e => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(challengeId || '');
                          }}
                          style={{ cursor: 'pointer', marginLeft: '8px' }}
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <title>Copy DC ID</title>
                          <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                          />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </p>
                      <p>
                        <strong>Event Date:</strong> {module.event_date}
                      </p>
                      <p>
                        <strong>Created:</strong>{' '}
                        {new Date(module.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <svg
                width="60"
                height="60"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
              <h3>No modules yet</h3>
              <p>Add your first module to get started</p>
            </div>
          )}
        </div>
      );
    } else if (activeTab === 'daily-modules') {
      return (
        <div className="modules-content">
          <div className="content-header">
            <div className="header-content">
              <h1 className="page-title">Daily Modules</h1>
              <p className="page-subtitle">Manage daily modules</p>
            </div>
            <button className="upload-excel-button" onClick={handleUploadExcel}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10,9 9,9 8,9" />
              </svg>
              Upload Excel
            </button>
            <button
              className="create-button"
              onClick={handleUpdateChallengePoints}
              disabled={updatingChallengePoints}
            >
              {updatingChallengePoints
                ? 'Updating...'
                : 'Update challenge points'}
            </button>
            <button className="create-button" onClick={handleAddModule}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Module
            </button>
          </div>
          {loading ? (
            <div className="empty-state">
              <div className="button-spinner"></div>
              <p>Loading modules...</p>
            </div>
          ) : dailyModules.length > 0 ? (
            <div className="modules-list">
              {dailyModules.map(module => (
                <div
                  key={module.id}
                  className="module-card"
                  onClick={() => handleModuleClick(module.id, 'daily')}
                >
                  <div className="module-card-header">
                    <div>
                      <h3>{module.title}</h3>
                      <p className="challenge-id">
                        DC ID: {challengeId}{' '}
                        <svg
                          onClick={e => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(challengeId || '');
                          }}
                          style={{ cursor: 'pointer', marginLeft: '4px' }}
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <title>Copy DC ID</title>
                          <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                          />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </p>
                    </div>
                    <div className="module-actions">
                      <button
                        className="edit-module-button"
                        onClick={e => {
                          e.stopPropagation();
                          handleEditDailyModule({
                            module_id: module.id,
                            title: module.title,
                            medal_share_image_url: module.medal_share_image_url,
                            medal_image_url: module.medal_image_url,
                            medal_joy_audio_url: module.medal_joy_audio_url,
                            medal_title: module.medal_title,
                            medal_subtitle: module.medal_subtitle,
                            medal_share_text: module.medal_share_text,
                            medal_share_url: module.medal_share_url,
                            ai_coach_context: module.ai_coach_context,
                            children_experiment_module_id:
                              module.children_experiment_module_id,
                            gold_medal_image_url: module.gold_medal_image_url,
                            silver_medal_image_url:
                              module.silver_medal_image_url,
                            bronze_medal_image_url:
                              module.bronze_medal_image_url,
                            total_available_points:
                              module.total_available_points,
                          });
                        }}
                        title="Edit module"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        className="delete-module-button"
                        onClick={e => {
                          e.stopPropagation();
                          handleDeleteDailyModule(module.id);
                        }}
                        title="Delete module"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="3,6 5,6 21,6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="module-card-body">
                    {module.medal_image_url && (
                      <img
                        src={module.medal_image_url}
                        alt="Medal"
                        className="module-image"
                      />
                    )}
                    <div className="module-info">
                      <p>
                        <strong>ID:</strong> {module.id}{' '}
                        <svg
                          onClick={e => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(module.id);
                          }}
                          style={{ cursor: 'pointer', marginLeft: '8px' }}
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <title>Copy ID</title>
                          <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                          />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </p>
                      <p>
                        <strong>DC ID:</strong> {challengeId}{' '}
                        <svg
                          onClick={e => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(challengeId || '');
                          }}
                          style={{ cursor: 'pointer', marginLeft: '8px' }}
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <title>Copy DC ID</title>
                          <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                          />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </p>
                      {module.medal_share_image_url && (
                        <p>
                          <strong>Share Image:</strong>{' '}
                          <a
                            href={module.medal_share_image_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        </p>
                      )}
                      {module.medal_joy_audio_url && (
                        <p>
                          <strong>Audio:</strong>{' '}
                          <a
                            href={module.medal_joy_audio_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Listen
                          </a>
                        </p>
                      )}
                      <p>
                        <strong>Created:</strong>{' '}
                        {new Date(module.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <svg
                width="60"
                height="60"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
              <h3>No modules yet</h3>
              <p>Add your first module to get started</p>
            </div>
          )}
        </div>
      );
    } else if (activeTab === 'coaches') {
      return (
        <div className="coaches-content">
          <div className="content-header">
            <div className="header-content">
              <h1 className="page-title">Coach</h1>
              <p className="page-subtitle">Manage challenge coaches</p>
            </div>
            <button className="create-button" onClick={handleAddCoach}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Coach
            </button>
          </div>
          {loading ? (
            <div className="empty-state">
              <div className="button-spinner"></div>
              <p>Loading coaches...</p>
            </div>
          ) : coaches.length > 0 ? (
            <div className="modules-list">
              {coaches.map(coach => (
                <div key={coach.id} className="module-card">
                  <div className="module-card-header">
                    <h3>{coach.name}</h3>
                    <button
                      className="edit-module-button"
                      onClick={() => handleEditCoach(coach)}
                      title="Edit coach"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </div>
                  <div className="module-card-body">
                    {coach.avatar_image_url && (
                      <img
                        src={coach.avatar_image_url}
                        alt="Coach Avatar"
                        className="module-image"
                      />
                    )}
                    <div className="module-info">
                      <p>
                        <strong>Tagline:</strong> {coach.tagline}
                      </p>
                      <p>
                        <strong>Language:</strong> {coach.language}
                      </p>
                      <p>
                        <strong>Status:</strong>{' '}
                        {coach.active ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <svg
                width="60"
                height="60"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <h3>No coaches yet</h3>
              <p>Add your first coach to get started</p>
            </div>
          )}
        </div>
      );
    } else if (activeTab === 'reset') {
      return (
        <div className="reset-content">
          <div className="content-header">
            <div className="header-content">
              <h1 className="page-title">Reset</h1>
              <p className="page-subtitle">Reset challenge data</p>
            </div>
          </div>
          <div className="reset-form-container">
            <div className="reset-form">
              {resetError && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {resetError}
                </div>
              )}
              {resetSuccess && (
                <div className="modal-success">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22,4 12,14.01 9,11.01" />
                  </svg>
                  {resetSuccess}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="phone_number">
                  Phone Number <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="phone_number"
                  value={resetForm.phone_number}
                  onChange={e =>
                    setResetForm({
                      ...resetForm,
                      phone_number: e.target.value,
                    })
                  }
                  placeholder="Enter phone number"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="day_to_reset">
                  Day to Reset To <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="day_to_reset"
                  value={resetForm.day_to_reset}
                  onChange={e =>
                    setResetForm({
                      ...resetForm,
                      day_to_reset: e.target.value,
                    })
                  }
                  placeholder="Enter day number"
                  className="form-input"
                  min="1"
                />
              </div>
              <div className="form-group">
                <label htmlFor="post_to_reset">
                  Post to Reset To <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="post_to_reset"
                  value={resetForm.post_to_reset}
                  onChange={e =>
                    setResetForm({
                      ...resetForm,
                      post_to_reset: e.target.value,
                    })
                  }
                  placeholder="Enter post ID"
                  className="form-input"
                />
              </div>
              <button
                type="button"
                className="btn-primary"
                onClick={handleResetChallenge}
                disabled={resetting}
              >
                {resetting ? (
                  <>
                    <div className="button-spinner"></div>
                    Resetting...
                  </>
                ) : (
                  'Reset Challenge'
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="daily-challenge-details-page">
      <div className="top-strip"></div>

      <div className="sidebar">
        <div className="sidebar-header">
          <h1 className="dashboard-title">Admin Dashboard</h1>
        </div>

        <div className="sidebar-divider"></div>

        <div className="user-info">
          <div className="user-avatar-small">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22,4 12,14.01 9,11.01" />
            </svg>
          </div>
          <div className="user-details">
            <h4 className="user-name">Daily Challenge</h4>
            <p className="user-email">Challenge ID: {challengeId}</p>
          </div>
        </div>

        <div className="sidebar-nav">
          <button
            className={`nav-button ${activeTab === 'daily-modules' ? 'active' : ''}`}
            onClick={() => setActiveTab('daily-modules')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21,15 16,10 5,21" />
            </svg>
            Daily Modules
          </button>
          <button
            className={`nav-button ${activeTab === 'date-modules' ? 'active' : ''}`}
            onClick={() => setActiveTab('date-modules')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            Date Specific Modules
          </button>
          <button
            className={`nav-button ${activeTab === 'coaches' ? 'active' : ''}`}
            onClick={() => setActiveTab('coaches')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            Coach
          </button>
          <button
            className={`nav-button ${activeTab === 'reset' ? 'active' : ''}`}
            onClick={() => setActiveTab('reset')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="23,4 23,10 17,10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
            Reset
          </button>
        </div>
      </div>

      <div className="main-content">{renderTabContent()}</div>

      {showDateModuleModal && (
        <div className="modal-overlay" onClick={handleCloseDateModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingDateModule
                  ? 'Edit Date Specific Module'
                  : 'Add Date Specific Module'}
              </h2>
              <button className="modal-close" onClick={handleCloseDateModal}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {error && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {error}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="event_date">
                  Event Date <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="event_date"
                  value={dateModuleForm.event_date}
                  onChange={e =>
                    setDateModuleForm({
                      ...dateModuleForm,
                      event_date: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="date_title">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="date_title"
                  value={dateModuleForm.title}
                  onChange={e =>
                    setDateModuleForm({
                      ...dateModuleForm,
                      title: e.target.value,
                    })
                  }
                  placeholder="Enter module title"
                  className="form-input"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseDateModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmitDateModule}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="button-spinner"></div>
                    {editingDateModule ? 'Updating...' : 'Creating...'}
                  </>
                ) : editingDateModule ? (
                  'Update Module'
                ) : (
                  'Create Module'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModuleModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingDailyModule ? 'Edit Daily Module' : 'Add Module'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {error && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {error}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="title">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  value={moduleForm.title}
                  onChange={e =>
                    setModuleForm({ ...moduleForm, title: e.target.value })
                  }
                  placeholder="Enter module title"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="medal_share_image_url">
                  Medal Share Image URL
                </label>
                <input
                  type="url"
                  id="medal_share_image_url"
                  value={moduleForm.medal_share_image_url}
                  onChange={e =>
                    setModuleForm({
                      ...moduleForm,
                      medal_share_image_url: e.target.value,
                    })
                  }
                  placeholder="https://example.com/image.jpg"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="medal_image_url">Medal Image URL</label>
                <input
                  type="url"
                  id="medal_image_url"
                  value={moduleForm.medal_image_url}
                  onChange={e =>
                    setModuleForm({
                      ...moduleForm,
                      medal_image_url: e.target.value,
                    })
                  }
                  placeholder="https://example.com/medal.jpg"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="medal_joy_audio_url">Medal Joy Audio URL</label>
                <input
                  type="url"
                  id="medal_joy_audio_url"
                  value={moduleForm.medal_joy_audio_url}
                  onChange={e =>
                    setModuleForm({
                      ...moduleForm,
                      medal_joy_audio_url: e.target.value,
                    })
                  }
                  placeholder="https://example.com/audio.mp3"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="medal_title">Medal Title</label>
                <input
                  type="text"
                  id="medal_title"
                  value={moduleForm.medal_title}
                  onChange={e =>
                    setModuleForm({
                      ...moduleForm,
                      medal_title: e.target.value,
                    })
                  }
                  placeholder="Enter medal title"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="medal_subtitle">Medal Subtitle</label>
                <input
                  type="text"
                  id="medal_subtitle"
                  value={moduleForm.medal_subtitle}
                  onChange={e =>
                    setModuleForm({
                      ...moduleForm,
                      medal_subtitle: e.target.value,
                    })
                  }
                  placeholder="Enter medal subtitle"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="medal_share_text">Medal Share Text</label>
                <input
                  type="text"
                  id="medal_share_text"
                  value={moduleForm.medal_share_text}
                  onChange={e =>
                    setModuleForm({
                      ...moduleForm,
                      medal_share_text: e.target.value,
                    })
                  }
                  placeholder="Enter medal share text"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="medal_share_url">Medal Share URL</label>
                <input
                  type="url"
                  id="medal_share_url"
                  value={moduleForm.medal_share_url}
                  onChange={e =>
                    setModuleForm({
                      ...moduleForm,
                      medal_share_url: e.target.value,
                    })
                  }
                  placeholder="https://example.com/share"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="ai_coach_context">AI Coach Context</label>
                <textarea
                  id="ai_coach_context"
                  value={moduleForm.ai_coach_context}
                  onChange={e =>
                    setModuleForm({
                      ...moduleForm,
                      ai_coach_context: e.target.value,
                    })
                  }
                  placeholder="Enter AI coach context"
                  className="form-input"
                  rows={4}
                />
              </div>
              <div className="form-group">
                <label>Children Experiment Module IDs</label>
                {moduleForm.children_experiment_module_id.map((id, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      marginBottom: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <input
                      type="text"
                      value={id}
                      onChange={e => {
                        const newIds = [
                          ...moduleForm.children_experiment_module_id,
                        ];
                        newIds[index] = e.target.value;
                        setModuleForm({
                          ...moduleForm,
                          children_experiment_module_id: newIds,
                        });
                      }}
                      placeholder="Enter module ID"
                      className="form-input"
                      style={{ marginRight: '8px' }}
                    />
                    {moduleForm.children_experiment_module_id.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newIds =
                            moduleForm.children_experiment_module_id.filter(
                              (_, i) => i !== index
                            );
                          setModuleForm({
                            ...moduleForm,
                            children_experiment_module_id: newIds,
                          });
                        }}
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        -
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setModuleForm({
                      ...moduleForm,
                      children_experiment_module_id: [
                        ...moduleForm.children_experiment_module_id,
                        '',
                      ],
                    });
                  }}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  + Add Module ID
                </button>
              </div>
              <div className="form-group">
                <label htmlFor="gold_medal_image_url">
                  Gold Medal Image URL
                </label>
                <input
                  type="url"
                  id="gold_medal_image_url"
                  value={moduleForm.gold_medal_image_url}
                  onChange={e =>
                    setModuleForm({
                      ...moduleForm,
                      gold_medal_image_url: e.target.value,
                    })
                  }
                  placeholder="https://example.com/gold-medal.jpg"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="silver_medal_image_url">
                  Silver Medal Image URL
                </label>
                <input
                  type="url"
                  id="silver_medal_image_url"
                  value={moduleForm.silver_medal_image_url}
                  onChange={e =>
                    setModuleForm({
                      ...moduleForm,
                      silver_medal_image_url: e.target.value,
                    })
                  }
                  placeholder="https://example.com/silver-medal.jpg"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="bronze_medal_image_url">
                  Bronze Medal Image URL
                </label>
                <input
                  type="url"
                  id="bronze_medal_image_url"
                  value={moduleForm.bronze_medal_image_url}
                  onChange={e =>
                    setModuleForm({
                      ...moduleForm,
                      bronze_medal_image_url: e.target.value,
                    })
                  }
                  placeholder="https://example.com/bronze-medal.jpg"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="total_available_points">
                  Total Available Points
                </label>
                <input
                  type="number"
                  id="total_available_points"
                  value={moduleForm.total_available_points}
                  onChange={e =>
                    setModuleForm({
                      ...moduleForm,
                      total_available_points: e.target.value,
                    })
                  }
                  placeholder="Enter total points"
                  className="form-input"
                  min="0"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmitModule}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="button-spinner"></div>
                    {editingDailyModule ? 'Updating...' : 'Creating...'}
                  </>
                ) : editingDailyModule ? (
                  'Update Module'
                ) : (
                  'Create Module'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <CoachModal
        coach={editingCoach}
        isOpen={showCoachModal}
        onClose={handleCloseCoachModal}
        onSave={handleSaveCoach}
      />

      {showConfirmModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowConfirmModal(false)}
        >
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Upload</h2>
              <button
                className="modal-close"
                onClick={() => setShowConfirmModal(false)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to upload{' '}
                <strong>{selectedFile?.name}</strong>?
              </p>
              <p>This will process the Excel file and create posts.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleConfirmUpload}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {uploadingExcel && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div
              className="modal-body"
              style={{ textAlign: 'center', padding: '40px' }}
            >
              <div
                className="button-spinner"
                style={{ margin: '0 auto 20px' }}
              ></div>
              <h3>Uploading Excel File...</h3>
              <p>Please wait while we process your file.</p>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowUploadModal(false)}
        >
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Result</h2>
              <button
                className="modal-close"
                onClick={() => setShowUploadModal(false)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {uploadResult?.error ? (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {uploadResult.error}
                </div>
              ) : (
                <div className="upload-success">
                  <div className="success-header">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22,4 12,14.01 9,11.01" />
                    </svg>
                    <h3>Upload Successful!</h3>
                  </div>
                  <div className="result-content">
                    <pre>{JSON.stringify(uploadResult, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-primary"
                onClick={() => setShowUploadModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyChallengeDetailsPage;
